<template>
  <div>
    <breadcrumbs
      :breadcrumb-items="breadcrumbItems"
    />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CampaignToolbar
        v-if="Object.keys(campaign).length !== 0"
        :campaign="campaign"
        closing
      />
      <hr>
      <div class="table-filters">
        <div class="col-type">
          <b-form-radio-group
            id="type-report"
            v-model="typeStats"
            class="mb-1"
            button-variant="outline-primary"
            :options="optionsRadioLocal"
            buttons
            size="sm"
            name="radios-btn-default"
            @change="changeType"
          />
        </div>
        <div
          v-if="typeStats === 'custom'"
          class="col-picker"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="CalendarIcon"
              size="16"
            />
            <flat-pickr
              v-model="rangePicker"
              :config="{mode: 'range', dateFormat: 'Y-m-d'}"
              class="form-control flat-picker bg-transparent border-0 shadow-none"
              placeholder="YYYY-MM-DD"
              @on-change="getCampaignStatsByType"
            />
          </div>
        </div>
        <div>
          <b-button
            v-b-tooltip.hover.top="$t('toggleMap')"
            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
            variant="outline-primary"
            size="sm"
            class="btn-icon"
            @click="toggleMap()"
          >
            <feather-icon
              icon="BarChart2Icon"
              size="16"
            />
          </b-button>
        </div>

      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else-if="rows.length">
        <ImpressionsStatsType
          v-if="showMap"
          :rows="rowsRevers"
          :type="typeStats"
          :range="rangePicker"
        />

        <b-table
          small
          :items="rows"
          :fields="filterFields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
          striped
          foot-clone
        >
          <template #head(datetime)>
            {{ $t('message.tableHeader.date') }}
          </template>

          <template #head(impressions)>
            {{ $t('stats.impressions') }}
          </template>

          <template #cell(datetime)="data">
            {{ typeStats === 'day' ? getFormattedDateTime(new Date(data.value)) : getFormattedDate(new Date(data.value)) }}
          </template>

          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #foot(datetime)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
          </template>
          <template #foot(impressions)="data">
            <strong :data-label="data.label">{{ totalFormat('impressions') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(totalFormat('impressions')) : 0 }}</strong>
          </template>
        </b-table>
      </div>
      <div
        v-else
        class="card-nodata"
      >
        <div class="card-body">
          {{ $t('sites.nodata_period') }}
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import {
  BCard,
  BTable,
  BFormRadioGroup,
  BSpinner,
  VBTooltip,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CampaignToolbar from '@/views/dashboard/campaigns/CampaignToolbar.vue'
import { getUserData } from '@/auth/utils'
import flatPickr from 'vue-flatpickr-component'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate, { getFormattedDateTime } from '@/utils/date-formatter'
import ImpressionsStatsType from './ImpressionsStatsType.vue'

export default {
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BFormRadioGroup,
    flatPickr,
    BSpinner,
    ImpressionsStatsType,
    BButton,
    UserToolbar,
    CampaignToolbar,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      authUserData: getUserData(),
      showMap: false,
      loading: true,
      account: {},
      breadcrumbItems: [],
      typeStats: 'week',
      campaign: [],
      rows: [],
      banner: [],
      fields: [
        { key: 'datetime', label: 'Date' },
        { key: 'impressions', label: 'Impressions' },
      ],
      sortBy: 'datetime',
      sortDesc: true,
      sortDirection: 'asc',
      currency: currencyFormatter,
      getFormattedDate,
      getFormattedDateTime,
      optionsRadio: [
        { text: 'Today', key: 'day', value: 'day' },
        { text: '7 days', key: 'week', value: 'week' },
        { text: 'Month', key: 'month', value: 'month' },
        { text: 'Choose period', key: 'period', value: 'custom' },
      ],
      rangePicker: [getFormattedDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), getFormattedDate(new Date())],
    }
  },
  computed: {
    rowsRevers() {
      const items = this.rows
      return items
    },
    optionsRadioLocal() {
      return this.optionsRadio.map(o => ({
        value: o.value,
        text: this.$t(`stats.${o.key}`),
      }))
    },
    totalAmount() {
      let totalAmount = 0
      this.rows.forEach(i => {
        totalAmount += i.amount
      })
      return totalAmount
    },
    filterFields() {
      if (!this.totalFormat('conversion')) {
        return this.fields.filter(f => f.key !== 'conversion')
      }
      return this.fields
    },
  },
  created() {
    this.getUserData()
    this.getCampaignStatsByType()
  },
  methods: {
    totalFormat(field) {
      let total = 0
      this.rows.forEach(i => {
        if (i[field]) {
          total += i[field]
        }
      })
      return total
    },
    async getUserData() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
      this.getCampaignData()
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      this.setBreabcrumbs()
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$i18n.t('Users'),
          to: '/users',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}`,
        },
        {
          text: 'campaigns',
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}`,
        },
        {
          text: this.$i18n.t('page.features.impression'),
          active: true,
        },
      ]
    },
    async getCampaignStatsByType() {
      let dateFrom
      let dateTo
      const urlSearchParams = new URLSearchParams(window.location.search)
      const urlParams = Object.fromEntries(urlSearchParams.entries())
      if (urlParams.type) {
        this.typeStats = urlParams.type
      }

      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      this.loading = true
      this.rows = []

      if (dateTo !== undefined && this.typeStats === 'custom') {
        this.$router.replace({
          name: 'dashboard-campaign-impressions',
          query: { type: this.typeStats, from: dateFrom, to: dateTo },
        }).catch(() => {})
      } else {
        this.$router.replace({
          name: 'dashboard-campaign-impressions',
          query: { type: this.typeStats },
        }).catch(() => {})
      }

      const params = {
        from: dateFrom,
        campaign_id: this.$route.params.id,
        to: dateTo || dateFrom,
        type: this.typeStats,
      }

      const responseData = await useJwt.getCampaignImpressionStatsByType(params)
      this.rows = responseData.data.impressionstats || []
      this.loading = false
    },
    changeType() {
      if (this.typeStats === 'custom') {
        let dateFrom
        let dateTo
        const selectedRange = this.rangePicker
        if (Array.isArray(selectedRange)) {
          [dateFrom, dateTo] = selectedRange
        } else {
          [dateFrom, , dateTo] = selectedRange.split(' ')
        }
        this.$router.replace({
          name: 'dashboard-campaign-impressions',
          query: { type: this.typeStats, from: dateFrom, to: dateTo },
        })
      } else {
        this.$router.replace({
          name: 'dashboard-campaign-impressions',
          query: { type: this.typeStats },
        })
      }

      this.getCampaignStatsByType()
    },
    toggleMap() {
      this.showMap = !this.showMap
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style>
  .table-filters {
    display: flex;
    justify-content: space-between;
  }

  tfoot th {
    background-color: #fff!important;
  }

  .col-picker {
    min-width: 220px;
  }

  @media (max-width: 1400px) {
    .table-filters {
      flex-wrap: wrap;
      margin-bottom: 15px;
    }

    .col-type {
      width: 60%;
    }
  }

  @media (max-width: 992px) {
    .col-type {
      width: 70%;
    }
  }

  @media (max-width: 800px) {
    .col-type {
      width: 100%;
    }
  }
</style>
